import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Link, Copy, Building2, Target, Brain, ShieldCheck } from 'lucide-react';
import { generateRegistrationLink, generateWorkBehaviorLink, generateCognitiveAssessmentLink, copyToClipboard } from '@/utils/registrationLinks';
import { useCompany } from '@/contexts/CompanyContext';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface AdminPageHeaderProps {
  title: string;
  showShareLinks?: boolean;
}

const AdminPageHeader: React.FC<AdminPageHeaderProps> = ({ 
  title,
  showShareLinks = true
}) => {
  const { currentCompany } = useCompany();
  const { toast } = useToast();
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const companySlug = currentCompany?.company_slug || null;

  const handleCopyLink = async (linkType: 'registration' | 'workBehavior' | 'cognitive') => {
    if (!companySlug) return;
    
    let link = '';
    let linkName = '';
    
    switch (linkType) {
      case 'registration':
        link = generateRegistrationLink(companySlug);
        linkName = 'Registration';
        break;
      case 'workBehavior':
        link = generateWorkBehaviorLink(companySlug);
        linkName = 'Work Behavior Assessment';
        break;
      case 'cognitive':
        link = generateCognitiveAssessmentLink(companySlug);
        linkName = 'Cognitive Assessment';
        break;
    }
    
    const success = await copyToClipboard(link);
    if (success) {
      toast({
        title: "Link Copied!",
        description: `${linkName} link copied to clipboard`
      });
    } else {
      toast({
        title: "Copy Failed",
        description: "Unable to copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="sticky top-0 z-50 bg-background backdrop-blur-sm bg-background/95 supports-[backdrop-filter]:bg-background/60">
      <div className="px-4 sm:px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SidebarTrigger className="h-9 w-9 hover:bg-muted text-foreground rounded-lg transition-all" />
            <h1 className="text-lg font-medium text-foreground tracking-tight">
              {title}
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Super Admin Button */}
            {isSuperAdmin() && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="gap-2 hover:bg-muted"
                onClick={() => navigate('/superadmin')}
              >
                <ShieldCheck className="h-4 w-4" />
                Super Admin
              </Button>
            )}
            
            {/* Assessment Links Dropdown */}
            {showShareLinks && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2 hover:bg-muted">
                    <Link className="h-4 w-4" />
                    Share Links
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 bg-background border shadow-lg z-50">
                  <DropdownMenuItem 
                    onClick={() => handleCopyLink('registration')}
                    className="flex items-center justify-between cursor-pointer bg-primary/5 border-l-2 border-primary"
                  >
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="font-medium">Full Assessment</span>
                      <Badge variant="secondary" className="text-xs px-1.5 py-0 flex-shrink-0">Recommended</Badge>
                    </div>
                    <Copy className="h-4 w-4 flex-shrink-0" />
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={() => handleCopyLink('workBehavior')}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <Target className="h-4 w-4 flex-shrink-0" />
                      <span>Work Behavior Only</span>
                    </div>
                    <Copy className="h-4 w-4 flex-shrink-0" />
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem 
                    onClick={() => handleCopyLink('cognitive')}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <Brain className="h-4 w-4 flex-shrink-0" />
                      <span>Cognitive Only</span>
                    </div>
                    <Copy className="h-4 w-4 flex-shrink-0" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPageHeader;
